import React, { useState, useEffect } from 'react';
import {
    Button,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
    Card,
    CardBody,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Badge,
    Table,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Plus, Edit3, Trash2, Star, CheckCircle } from 'react-feather';
import http from '../../components/network';

const emptyTax = {
    id: null,
    nama: '',
    rate: '',
    tipe: 'persen',
    is_active: true,
    is_default: false,
};

const MasterTax = () => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [taxes, setTaxes] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyTax);

    // ══════════════════════════════════════════════════
    // Fetch Data
    // ══════════════════════════════════════════════════
    const fetchTaxes = async () => {
        setLoading(true);
        try {
            const { data } = await http.get('/taxes');
            setTaxes(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch taxes:', err);
            Swal.fire('Error', 'Gagal memuat data tax', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTaxes();
    }, []);

    // ══════════════════════════════════════════════════
    // Modal Handlers
    // ══════════════════════════════════════════════════
    const openModal = (tax = null) => {
        if (tax) {
            setForm({
                id: tax.id,
                nama: tax.nama,
                rate: parseFloat(tax.rate),
                tipe: tax.tipe,
                is_active: tax.is_active,
                is_default: tax.is_default,
            });
        } else {
            setForm(emptyTax);
        }
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setForm(emptyTax);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // ══════════════════════════════════════════════════
    // CRUD
    // ══════════════════════════════════════════════════
    const handleSubmit = async () => {
        if (!form.nama || form.rate === '') {
            Swal.fire('Error!', 'Nama dan rate wajib diisi.', 'error');
            return;
        }

        setSubmitting(true);

        const payload = {
            nama: form.nama,
            rate: parseFloat(form.rate) || 0,
            tipe: form.tipe,
            is_active: form.is_active,
            is_default: form.is_default,
        };

        try {
            if (form.id) {
                await http.put(`/taxes/${form.id}`, payload);
                Swal.fire('Success!', 'Tax berhasil diupdate.', 'success');
            } else {
                await http.post('/taxes', payload);
                Swal.fire('Success!', 'Tax berhasil ditambahkan.', 'success');
            }
            await fetchTaxes();
            closeModal();
        } catch (err) {
            const msg = err.response?.data?.message || 'Terjadi kesalahan';
            Swal.fire('Error!', msg, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (tax) => {
        if (tax.is_default) {
            Swal.fire('Info', 'Tax default tidak bisa dihapus. Ubah default dulu.', 'info');
            return;
        }

        const result = await Swal.fire({
            title: 'Yakin hapus?',
            text: `Hapus tax "${tax.nama}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Ya, hapus!',
            cancelButtonText: 'Batal',
        });

        if (!result.isConfirmed) return;

        try {
            await http.delete(`/taxes/${tax.id}`);
            Swal.fire('Deleted!', 'Tax berhasil dihapus.', 'success');
            await fetchTaxes();
        } catch (err) {
            const msg = err.response?.data?.message || 'Gagal menghapus';
            Swal.fire('Error!', msg, 'error');
        }
    };

    // Set default via tombol bintang (fallback: pakai PUT update)
    const handleSetDefault = async (tax) => {
        const result = await Swal.fire({
            title: 'Set sebagai default?',
            text: `"${tax.nama}" akan jadi tax default untuk transaksi baru.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Ya, set default',
            cancelButtonText: 'Batal',
        });

        if (!result.isConfirmed) return;

        try {
            // Kalau backend punya endpoint khusus set-default, pakai ini:
            // await http.post(`/taxes/${tax.id}/set-default`);

            // Fallback: pakai PUT update
            await http.put(`/taxes/${tax.id}`, {
                nama: tax.nama,
                rate: tax.rate,
                tipe: tax.tipe,
                is_active: true,
                is_default: true,
            });

            Swal.fire('Success!', 'Default tax berhasil diubah.', 'success');
            await fetchTaxes();
        } catch (err) {
            const msg = err.response?.data?.message || 'Gagal set default';
            Swal.fire('Error!', msg, 'error');
        }
    };

    // ══════════════════════════════════════════════════
    // Render
    // ══════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="p-3 text-center py-5">
                <Spinner color="primary" />
                <p className="mt-3 text-muted">Memuat data tax...</p>
            </div>
        );
    }

    return (
        <div className="p-3 bg-light min-vh-100">
            {/* ═══════════ PAGE HEADER ═══════════ */}
            <div className="mb-4">
                <h3 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>
                    <span style={{ fontSize: '26px' }}>💰</span> {t('Tax Master')}
                </h3>
                <p className="mb-0 text-muted small">
                    {t('Manage tax rates and defaults')}
                </p>
            </div>

            <Row className="g-4">
                <Col lg="12">
                    <Card
                        style={{
                            border: 'none',
                            borderRadius: '14px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            overflow: 'hidden',
                        }}
                    >
                        <CardBody className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: '48px', height: '48px', background: '#fef3c7' }}
                                    >
                                        <span style={{ fontSize: '22px' }}>💰</span>
                                    </div>
                                    <div>
                                        <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
                                            {t('Tax List')}
                                        </h5>
                                        <small className="text-muted">
                                            {taxes.length} {t('tax registered')}
                                        </small>
                                    </div>
                                </div>
                                <Button
                                    color="success"
                                    onClick={() => openModal()}
                                    className="d-flex align-items-center gap-2 fw-bold text-white"
                                    style={{
                                        borderRadius: '10px',
                                        padding: '10px 20px',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        boxShadow: '0 2px 6px rgba(16, 185, 129, 0.25)',
                                    }}
                                >
                                    <Plus size={16} /> {t('Add Tax')}
                                </Button>
                            </div>

                            <div
                                style={{
                                    background: '#fff',
                                    borderRadius: '10px',
                                    overflow: 'hidden',
                                    border: '1px solid #e2e8f0',
                                }}
                            >
                                <Table hover responsive className="mb-0" style={{ fontSize: '13px' }}>
                                    <thead style={{ background: '#f8fafc' }}>
                                        <tr>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px' }} width="60">
                                                #
                                            </th>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px' }}>
                                                {t('Name')}
                                            </th>
                                            <th className="text-end" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px' }} width="140">
                                                {t('Rate')}
                                            </th>
                                            <th style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px' }} width="130">
                                                {t('Type')}
                                            </th>
                                            <th className="text-center" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px' }} width="120">
                                                {t('Status')}
                                            </th>
                                            <th className="text-center" style={{ fontWeight: 700, color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px' }} width="150">
                                                {t('Actions')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {taxes.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="text-center text-muted py-5">
                                                    <div style={{ fontSize: '40px', marginBottom: '8px' }}>📭</div>
                                                    <div>{t('No tax yet')}</div>
                                                    <small className="text-muted">
                                                        {t('Click "Add Tax" to create one')}
                                                    </small>
                                                </td>
                                            </tr>
                                        ) : (
                                            taxes.map((tax, index) => (
                                                <tr key={tax.id}>
                                                    <td style={{ padding: '12px', color: '#6b7280' }}>
                                                        {index + 1}
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                                                {tax.nama}
                                                            </span>
                                                            {tax.is_default && (
                                                                <Badge
                                                                    pill
                                                                    style={{
                                                                        background: '#dbeafe',
                                                                        color: '#1e40af',
                                                                        fontSize: '10px',
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    ⭐ {t('DEFAULT')}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-end" style={{ padding: '12px', fontWeight: 600, color: '#0ea5e9' }}>
                                                        {tax.tipe === 'persen'
                                                            ? `${parseFloat(tax.rate).toFixed(2)}%`
                                                            : `Rp ${new Intl.NumberFormat('id-ID').format(tax.rate)}`}
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <Badge
                                                            pill
                                                            style={{
                                                                background: tax.tipe === 'persen' ? '#dbeafe' : '#fef3c7',
                                                                color: tax.tipe === 'persen' ? '#1e40af' : '#92400e',
                                                                fontSize: '11px',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {tax.tipe === 'persen' ? 'Persen' : 'Nominal'}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-center" style={{ padding: '12px' }}>
                                                        {tax.is_active ? (
                                                            <Badge
                                                                pill
                                                                style={{
                                                                    background: '#d1fae5',
                                                                    color: '#065f46',
                                                                    fontSize: '11px',
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                <CheckCircle size={10} className="me-1" /> {t('Active')}
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                pill
                                                                style={{
                                                                    background: '#f3f4f6',
                                                                    color: '#6b7280',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {t('Inactive')}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        {!tax.is_default && (
                                                            <Button
                                                                color="light"
                                                                size="sm"
                                                                className="border d-inline-flex align-items-center justify-content-center me-1"
                                                                style={{ width: '32px', height: '32px', padding: 0 }}
                                                                onClick={() => handleSetDefault(tax)}
                                                                title={t('Set as Default')}
                                                            >
                                                                <Star size={13} className="text-warning" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            color="light"
                                                            size="sm"
                                                            className="border d-inline-flex align-items-center justify-content-center me-1"
                                                            style={{ width: '32px', height: '32px', padding: 0 }}
                                                            onClick={() => openModal(tax)}
                                                            title={t('Edit')}
                                                        >
                                                            <Edit3 size={13} className="text-primary" />
                                                        </Button>
                                                        <Button
                                                            color="light"
                                                            size="sm"
                                                            className="border d-inline-flex align-items-center justify-content-center"
                                                            style={{ width: '32px', height: '32px', padding: 0 }}
                                                            onClick={() => handleDelete(tax)}
                                                            disabled={tax.is_default}
                                                            title={
                                                                tax.is_default
                                                                    ? 'Tax default tidak bisa dihapus'
                                                                    : t('Delete')
                                                            }
                                                        >
                                                            <Trash2
                                                                size={13}
                                                                className={tax.is_default ? 'text-muted' : 'text-danger'}
                                                            />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ═══════════ MODAL ADD/EDIT TAX ═══════════ */}
            <Modal isOpen={modalOpen} toggle={closeModal} size="md" centered>
                <ModalHeader
                    toggle={closeModal}
                    style={{
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        color: '#fff',
                        borderBottom: 'none',
                        borderRadius: '14px 14px 0 0',
                        padding: '18px 24px',
                    }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '20px' }}>💰</span>
                        {form.id ? t('Edit Tax') : t('Add Tax')}
                    </div>
                </ModalHeader>
                <ModalBody style={{ padding: '24px' }}>
                    <FormGroup>
                        <Label for="taxNama" className="small fw-semibold text-muted">
                            {t('Name')} <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="taxNama"
                            name="nama"
                            value={form.nama}
                            onChange={handleChange}
                            placeholder="PPN 11%"
                            style={{ borderRadius: '10px' }}
                        />
                    </FormGroup>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="taxRate" className="small fw-semibold text-muted">
                                    {t('Rate')} <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="number"
                                    id="taxRate"
                                    name="rate"
                                    value={form.rate}
                                    onChange={handleChange}
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    placeholder="11"
                                    style={{ borderRadius: '10px' }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="taxTipe" className="small fw-semibold text-muted">
                                    {t('Type')}
                                </Label>
                                <Input
                                    type="select"
                                    id="taxTipe"
                                    name="tipe"
                                    value={form.tipe}
                                    onChange={handleChange}
                                    style={{ borderRadius: '10px' }}
                                >
                                    <option value="persen">{t('Percent (%)')}</option>
                                    <option value="nominal">{t('Nominal (Rp)')}</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup check className="mt-3">
                        <Label check>
                            <Input
                                type="checkbox"
                                name="is_active"
                                checked={form.is_active}
                                onChange={handleChange}
                            />{' '}
                            {t('Active')}
                        </Label>
                    </FormGroup>

                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                name="is_default"
                                checked={form.is_default}
                                onChange={handleChange}
                            />{' '}
                            {t('Set as default tax')}
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter style={{ borderTop: '1px solid #e2e8f0', padding: '14px 24px' }}>
                    <Button
                        color="light"
                        onClick={closeModal}
                        disabled={submitting}
                        className="border"
                        style={{ borderRadius: '10px' }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="text-white"
                        style={{
                            borderRadius: '10px',
                            padding: '10px 24px',
                            fontWeight: 600,
                            border: 'none',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            boxShadow: '0 2px 6px rgba(16, 185, 129, 0.25)',
                        }}
                    >
                        {submitting ? <Spinner size="sm" className="me-2" /> : null}
                        {form.id ? t('Update') : t('Save')}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default MasterTax;